import { useEffect, useState } from 'react';
import {
  api,
  type ArchiveDay,
  type ArchiveSegment,
  type FlightTrack,
  type RouteDetail,
} from '../lib/api';
import { fmt } from '../lib/altitude';

interface ArchiveHistoryProps {
  hex: string;
  onReplayTrack: (track: FlightTrack) => void;
}

function utcToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function segmentToTrack(hex: string, seg: ArchiveSegment, route?: RouteDetail | null): FlightTrack {
  return {
    flight: {
      id: -1,
      hex,
      callsign: seg.callsign,
      status: 'closed',
      started_at: seg.start_ts,
      ended_at: seg.end_ts,
      origin_icao: route?.origin?.icao ?? null,
      dest_icao: route?.destination?.icao ?? null,
      max_alt_ft: seg.max_alt_ft,
      distance_km: null,
      duration_s: seg.duration_s,
    },
    points: seg.points,
  };
}

/**
 * Rückwirkende Tages-Historie aus dem adsb.lol-Archiv — funktioniert für
 * jeden Flieger, unabhängig von der eigenen Watchlist.
 */
export default function ArchiveHistory({ hex, onReplayTrack }: ArchiveHistoryProps) {
  const [date, setDate] = useState(utcToday);
  const [day, setDay] = useState<ArchiveDay | null>(null);
  const [loading, setLoading] = useState(false);
  // Start/Ziel je Callsign (adsbdb-Routen, im Worker 30 Tage gecacht)
  const [routes, setRoutes] = useState<Record<string, RouteDetail | null>>({});
  const today = utcToday();

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setDay(null);
    api
      .archive(hex, date, ctrl.signal)
      .then(setDay)
      .catch(() => setDay(null))
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [hex, date]);

  // Routen (Start → Ziel) zu den Callsigns der angezeigten Segmente nachladen
  useEffect(() => {
    if (!day || day.segments.length === 0) return;
    const ctrl = new AbortController();
    const callsigns = [...new Set(day.segments.map((s) => s.callsign).filter((c): c is string => !!c))];
    setRoutes({});
    for (const cs of callsigns) {
      api
        .route(cs, ctrl.signal)
        .then((r) => setRoutes((prev) => ({ ...prev, [cs]: r })))
        .catch(() => {});
    }
    return () => ctrl.abort();
  }, [day]);

  return (
    <div className="archive">
      <div className="archive-nav">
        <button
          className="archive-btn mono"
          onClick={() => setDate(shiftDate(date, -1))}
          aria-label="Tag zurück"
        >
          ◀
        </button>
        <input
          className="mono"
          type="date"
          value={date}
          max={today}
          onChange={(e) => e.target.value && setDate(e.target.value)}
        />
        <button
          className="archive-btn mono"
          onClick={() => setDate(shiftDate(date, 1))}
          disabled={date >= today}
          aria-label="Tag vor"
        >
          ▶
        </button>
      </div>

      {loading ? (
        <p className="muted history-note">Lade Archiv …</p>
      ) : !day ? (
        <p className="muted history-note">Archiv nicht erreichbar.</p>
      ) : day.segments.length === 0 ? (
        <p className="muted history-note">
          {day.available === false
            ? 'Für diesen Tag liegt keine Aufzeichnung vor.'
            : 'An diesem Tag keine Flüge aufgezeichnet.'}
        </p>
      ) : (
        <ul className="flight-list">
          {day.segments.map((seg) => {
            const route = seg.callsign ? routes[seg.callsign] : null;
            const routeLabel =
              route?.origin || route?.destination
                ? `${route.origin?.icao ?? '????'}→${route.destination?.icao ?? '????'}`
                : (seg.callsign ?? '—');
            return (
              <li key={seg.start_ts}>
                <button
                  className="flight-row"
                  onClick={() => onReplayTrack(segmentToTrack(hex, seg, route))}
                  title={
                    route?.origin || route?.destination
                      ? `${route?.origin?.name ?? '?'} → ${route?.destination?.name ?? '?'}`
                      : 'Track auf der Karte abspielen'
                  }
                >
                  <span className="flight-date mono">
                    {fmt.time(seg.start_ts)}–{fmt.time(seg.end_ts)}
                  </span>
                  <span className="flight-route mono">{routeLabel}</span>
                  <span className="flight-stats mono">
                    {fmt.duration(seg.duration_s)} · {fmt.alt(seg.max_alt_ft ?? undefined)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
